import React from "react";
import PageHero, { FeatureList } from "@/components/PageHero";
import { CTAFooter } from "@/pages/Prestations";
import { CatalogPackages } from "@/components/CatalogPackages";
import { useSeo, JsonLd } from "@/lib/seo";
import { HeartHandshake, Users, PartyPopper, Volume2, Users2, Sparkles, Trophy } from "lucide-react";

export default function Associations() {
  useSeo({
    title: "DJ association Nord & Hauts-de-France | My Animation",
    description:
      "Animation DJ pour soirées associatives, repas dansants, fêtes de village, bals et événements publics dans le Nord, le Pas-de-Calais et les Hauts-de-France.",
    path: "/associations",
    keywords: "DJ association Nord, DJ fête de village, DJ repas dansant, DJ bal Hauts-de-France",
  });

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Animation DJ pour associations",
    provider: { "@type": "LocalBusiness", name: "My Animation" },
    areaServed: "Hauts-de-France, Nord, Pas-de-Calais, Belgique",
    description: "Animations pour soirées associatives, repas dansants, fêtes de village, bals et animations publiques.",
  };

  return (
    <>
      <JsonLd data={serviceLd} />
      <PageHero
        eyebrow="Associations"
        title={<>Au service des <span className="italic gold-text">associations.</span></>}
        subtitle="Animations pour soirées associatives, repas dansants, événements locaux, fêtes de village, bals et animations publiques dans tout le Nord, le Pas-de-Calais et les Hauts-de-France."
        img="https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?auto=format&fit=crop&w=1600&q=80"
      />

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 reveal">
            <span className="eyebrow">Soutien aux associations</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.02]">
              Une animation <br/><span className="italic gold-text">à votre image.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 reveal">
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              My Animation accompagne les associations, comités des fêtes et structures
              locales pour leurs événements publics ou privés. De la fête de village
              au repas dansant en salle communale, nous adaptons notre prestation au
              budget, au public et au format souhaité — à Bousbecque, Lille, Tourcoing,
              Roubaix, Halluin, Comines, Wervicq-Sud, Hazebrouck, Armentières et dans
              toute la région.
            </p>
            <div className="mt-10">
              <FeatureList items={[
                "Tarif associatif sur devis",
                "Sonorisation extérieure & intérieure",
                "Micros pour discours et animations",
                "Animation tous publics",
                "Karaoké & blind test possibles",
                "Mise en lumière selon le lieu",
                "Coordination avec le comité des fêtes",
                "Intervention sur tout le Nord, Pas-de-Calais et Belgique",
              ]} />
            </div>
          </div>
        </div>
      </section>

      <CatalogPackages
        category="association"
        title="Nos forfaits associations"
        subtitle={(n) => (<>{n || "Nos"} formule{n > 1 ? "s" : ""}, <span className="italic gold-text">à votre échelle.</span></>)}
        icons={[Users2, Sparkles, Trophy]}
        testidPrefix="asso-pack-"
        featuredBadgeLabel="Le plus choisi"
        ctaLabel="Pré-réserver ce forfait"
        footerNote="Tarifs TTC · Devis personnalisé pour votre association"
      />

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-14 reveal">
            <span className="eyebrow">Événements types</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">Pour <span className="italic gold-text">chaque temps fort.</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { i: PartyPopper, t: "Fête de village", d: "Bal populaire, animation tous âges, ambiance conviviale." },
              { i: Users, t: "Repas dansant", d: "Soirée associative, fond musical pour repas puis bal." },
              { i: HeartHandshake, t: "Soirée caritative", d: "Animation rythmée pour gala associatif ou soirée solidaire." },
              { i: Volume2, t: "Animation publique", d: "Sonorisation et micros pour annonces, discours, jeux." },
            ].map(({ i: Icon, t, d }) => (
              <div key={t} className="card-luxe p-7 reveal">
                <Icon size={28} strokeWidth={1} className="text-[#DCB457] mb-5" />
                <h3 className="font-serif-luxe text-2xl">{t}</h3>
                <p className="text-white/55 mt-2 text-sm leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTAFooter />
    </>
  );
}
